/**
 * This file implements the client-side rendering for our react entrypoints.
 * This is used through the `reactMount` call in ./react-mount.js and should not
 * be invoked otherwise.
 *
 * The exported method from this file is responsible for rendering a component.
 */
import * as React from "react";
import {createRoot} from "react-dom/client";

import {inSandbox, Sandbox} from "@khan/testing-sandbox";

/**
 * Render or rehydrate a component in a given element.
 *
 * This call renders a given component in the element with the provided
 * `elementId`.
 *
 * @param Component The component to be rendered.
 * @param elementId The identifier of the DOM element where the
 * component is to be rendered or rehydrated.
 */
const reactMount = (Component: React.ComponentType<any>, elementId: string) => {
    // Don't allow reactMount inside of sandbox environments like Storybook.
    // This is because we're loading modules from chunks at runtime and some of
    // those chunks are also entry points but we don't want the entry-point
    // code running.
    if (inSandbox() !== Sandbox.None) {
        return;
    }

    // We are client-side rendering, so we find our mounting point and then
    // render into that container, including the
    // GqlRouter to ensure data requirements are fulfilled.
    const domContainer = document.getElementById(elementId);
    if (!domContainer) {
        throw new Error(
            `Element with id "${elementId}" was not found in the DOM`,
        );
    }

    // We are the one place where we should be calling createRoot and
    // we are always going to call this if we're not hydrating.
    // This should only be happening once for the given container - we should
    // not be hitting this more than once.
    // eslint-disable-next-line no-restricted-syntax
    const root = createRoot(domContainer);
    root.render(<Component />);
};

export default reactMount;
